import { Query, QueryCache, QueryClient } from "@tanstack/react-query";
import {
  DesignDto,
  DesignLogoDto,
  HeatingProductRefDto,
  MultiProductRefDto,
} from "../models/responses/DesignDto";
import { LogoDto } from "../models/responses/LogoDto";
import { PriceDto } from "../models/responses/PriceDto";
import { ApprovalGroupDto, ApprovalStatus } from "../models/responses/ApprovalDto";
import { PagedResponse } from "../models/responses/pagedResponse";
import { mapToDesign } from "../models/mappers/mapDesign";
import { DesignStatus } from "../../core/models/entities/enums/DesignStatus";
import { isDesignDetailQueryKey } from "../../utils/functions/storageFreshness";

// Response-patch for the design approval mutations (flag approvalPatchEnabled).
//
// PUT .../approval and POST .../approval-start answer with the full DesignDto
// of the design AFTER the operation - the truth for approval, status, MUL,
// serials and the logos' approval state.
//
// PRICES - the rule, per price container (product.totalPrices + products,
// heating.prices, designLogos[].logo.product.prices):
//   * the response's container holds at least one ADJUSTED price
//     (adjustedPrice > 0) -> the response wins. Only AdjustPrice sets
//     ProductPrice.AdjustedPrice and MultiProductRef.TotalPrices (both
//     [NotMapped] in the API), and CommitDesignAsync runs it right after the
//     MUL is created in NAV (DesignService.cs, MUL-creation branch) - so an
//     approval-start, a skip and the vendor approval that creates the MUL
//     answer with the new MUL's adjusted prices and any heating created on
//     the way. Keeping the cache's pre-commit prices there would show the
//     wrong price in the list, the popup and the cart for up to five
//     minutes.
//   * otherwise -> the cache wins. The non-commit paths never run
//     AdjustPrice: PlaceVerdictAsync has it commented out (reject, a partial
//     approval) and CommitDesignAsync skips it when the MUL already exists
//     (the customer's approval after the vendor's, a re-approval). Their
//     DesignDto still carries the PERSISTED price rows (Design_ProductPrice,
//     Design_HeatingPrices, Logo_ProductPrice via ProductPriceConverter,
//     which filters nothing): NON-EMPTY lists with adjustedPrice 0 and an
//     empty totalPrices. They carry no information, while the cache holds
//     the GetDesign/GetDesigns prices - a plain "non-empty set wins" rule
//     would empty totalPrices and write 0-prices into heating and logos on
//     every reject. An empty set is treated the same way (no information).
// STORAGE (inStorage, numberLogosInStorage, storageUpdatedAtUtc) is always
// kept from the cache: the verdict mapping runs without the storage-ledger
// context GetDesign/GetDesigns use.
//
// Same helpers for every cache shape: list pages (raw DTO rows), the detail
// entry (raw DTO under DesignRepository.useDesign) and the cart's entry (a
// mapped Design under the very same key, via useCartSync).
//
// FRESHNESS: a patch never extends a query's freshness (setQueryData keeps
// the entry's own dataUpdatedAt) and never clears an invalidation, so the
// list revalidates exactly when it would have without the patch. A query
// that is FETCHING while the patch lands gets the patch re-applied when its
// (possibly pre-verdict) result arrives - see reapplyWhenInFlightLands.

type AnyRecord = Record<string, unknown>;

// At least one row AdjustPrice has priced. A persisted-only row (the
// non-commit paths) arrives with adjustedPrice 0 - see the rule above.
export function hasAdjustedPrices(prices: unknown): boolean {
  return (
    Array.isArray(prices) &&
    prices.some(
      (p) =>
        !!p &&
        typeof (p as PriceDto).adjustedPrice === "number" &&
        (p as PriceDto).adjustedPrice > 0
    )
  );
}

export function productHasAdjustedPrices(
  product: MultiProductRefDto | null | undefined
): boolean {
  if (!product) return false;
  if (hasAdjustedPrices(product.totalPrices)) return true;
  return (product.products ?? []).some((p) => hasAdjustedPrices(p?.prices));
}

export function heatingHasAdjustedPrices(
  heating: HeatingProductRefDto | null | undefined
): boolean {
  return !!heating && hasAdjustedPrices(heating.prices);
}

// Fields the mutation itself changes - always taken from the response, even
// when the response says null (a skip clears the approval, for instance).
const ALWAYS_FROM_RESPONSE: ReadonlyArray<keyof DesignDto> = [
  "approval",
  "approvalType",
  "status",
  "isEditable",
  "updateAtUtc",
];

// Generic layer: the cached object with every non-null response field on
// top. A null/undefined in the response never erases a cached value (the
// verdict DTO omits the detail-only fields such as canClone, viewImages and
// the placed row's view).
function overlayNonNull<T extends AnyRecord>(cached: T, fresh: T): T {
  const merged: AnyRecord = { ...cached };
  Object.keys(fresh).forEach((key) => {
    const value = fresh[key];
    if (value !== null && value !== undefined) merged[key] = value;
  });
  return merged as T;
}

export function mergeLogoDto(
  cached: LogoDto | undefined,
  fresh: LogoDto | undefined
): LogoDto | undefined {
  if (!cached) return fresh;
  if (!fresh) return cached;
  const merged = overlayNonNull(
    cached as unknown as AnyRecord,
    fresh as unknown as AnyRecord
  ) as unknown as LogoDto;
  // Prices: adjusted rows in the response are the post-commit AdjustPrice
  // result and win; persisted-only rows (adjustedPrice 0) or an empty set
  // carry no information, so the cached container stays as it is.
  if (hasAdjustedPrices(fresh.product?.prices)) {
    merged.product = fresh.product;
  } else if (cached.product) {
    merged.product = cached.product;
  }
  // Storage: the response lacks the ledger context - keep the counts and
  // stamps the list/freshness flow maintains.
  if (cached.inStorage !== null && cached.inStorage !== undefined) {
    merged.inStorage = cached.inStorage;
  }
  if (Array.isArray(cached.numberLogosInStorage)) {
    merged.numberLogosInStorage = cached.numberLogosInStorage;
  }
  if (cached.storageUpdatedAtUtc !== undefined) {
    merged.storageUpdatedAtUtc = cached.storageUpdatedAtUtc;
  }
  return merged;
}

function mergeDesignLogos(
  cached: DesignLogoDto[] | undefined,
  fresh: DesignLogoDto[] | undefined
): DesignLogoDto[] | undefined {
  if (!Array.isArray(fresh)) return cached;
  const cachedList = Array.isArray(cached) ? cached : [];
  // Same "empty = no information" rule as for prices: a response without
  // logos never empties a cached design (a verdict cannot remove logos).
  if (fresh.length === 0 && cachedList.length > 0) return cached;
  return fresh.map((freshDl) => {
    const cachedDl = cachedList.find(
      (c) =>
        c &&
        ((c.id !== undefined && c.id === freshDl.id) ||
          (c.logoId !== undefined && c.logoId === freshDl.logoId))
    );
    if (!cachedDl) return freshDl;
    const merged = overlayNonNull(
      cachedDl as unknown as AnyRecord,
      freshDl as unknown as AnyRecord
    ) as unknown as DesignLogoDto;
    merged.logo = mergeLogoDto(cachedDl.logo, freshDl.logo) as LogoDto;
    return merged;
  });
}

export function mergeProduct(
  cached: MultiProductRefDto | null | undefined,
  fresh: MultiProductRefDto | null | undefined
): MultiProductRefDto | null | undefined {
  if (productHasAdjustedPrices(fresh)) {
    // Commit path: the response's totalPrices AND products are the adjusted
    // post-commit set (taken as one container, never mixed with the cache).
    return { ...(cached ?? {}), ...fresh! };
  }
  if (cached) {
    // Non-commit path: the cached container stays (its GetDesign/GetDesigns
    // prices - or its emptiness, never the response's persisted-only rows);
    // the MUL can still be new.
    return { ...cached, mul: fresh?.mul ?? cached.mul };
  }
  return fresh ?? cached;
}

export function mergeHeating(
  cached: HeatingProductRefDto | null | undefined,
  fresh: HeatingProductRefDto | null | undefined
): HeatingProductRefDto | null | undefined {
  if (heatingHasAdjustedPrices(fresh)) return fresh;
  return cached ?? fresh;
}

// The cached design DTO updated with the response: approval/status fields
// from the response, prices per the container rule above, storage from the
// cache, nothing erased by a missing field. Works on a raw DTO and on a
// mapped Design alike (the mapped fields are simply carried through).
export function mergeDesignDto(cached: DesignDto, fresh: DesignDto): DesignDto {
  const merged = overlayNonNull(
    cached as unknown as AnyRecord,
    fresh as unknown as AnyRecord
  ) as unknown as DesignDto;
  ALWAYS_FROM_RESPONSE.forEach((key) => {
    (merged as unknown as AnyRecord)[key] = (fresh as unknown as AnyRecord)[
      key
    ];
  });
  merged.product = mergeProduct(cached.product, fresh.product) as MultiProductRefDto;
  merged.heating = mergeHeating(cached.heating, fresh.heating) as HeatingProductRefDto;
  merged.designLogos = mergeDesignLogos(
    cached.designLogos,
    fresh.designLogos
  ) as DesignLogoDto[];
  return merged;
}

// The cart's entry (useCartSync) holds a mapped Design under the detail key;
// DesignRepository.useDesign holds the raw DTO and maps in `select`. Write
// back in the shape the entry already has so both readers keep working.
export function isMappedDesign(value: unknown): boolean {
  return (
    !!value &&
    typeof value === "object" &&
    (value as { updateAtUtc?: unknown }).updateAtUtc instanceof Date
  );
}

export function patchDesignEntry(current: unknown, fresh: DesignDto): unknown {
  if (current === null || current === undefined) return current;
  const merged = mergeDesignDto(current as DesignDto, fresh);
  return isMappedDesign(current) ? mapToDesign(merged) : merged;
}

function isPagedResponse(value: unknown): value is PagedResponse<DesignDto> {
  return (
    !!value &&
    typeof value === "object" &&
    Array.isArray((value as PagedResponse<DesignDto>).results)
  );
}

// --- Server-side status filter -------------------------------------------
//
// The design list can be filtered by "stateFilter" (Draft/Pending/Approved/
// Rejected - LogoState values in the FE, SortableState in the API). The API
// stores the value per design in FitSortAndFilterProps (DesignService.cs)
// and filters on it in DesignRepository.cs. A verdict changes it, so a row
// on a filtered page has to LEAVE the page when its new state no longer
// matches the filter. The mapping below is the API's, replicated exactly:
//   status == Approval && approval != null -> approval.OverAll
//   else Draft/Unknown -> Draft, Approval -> Pending, Done -> Approved,
//        Rejected -> Rejected
// and ApprovalGroup.OverAll (ApprovalGroup.cs): every present party
// Approved -> Approved; any party Rejected -> Rejected; else Pending.
// (Deliberately NOT the FE's GetOverallApprovalStatus, whose vendor-override
// rule differs from the server's.)
export type SortableState = "Draft" | "Pending" | "Approved" | "Rejected";

export function apiOverallApprovalStatus(
  approval: ApprovalGroupDto | null | undefined
): ApprovalStatus {
  const parties = [approval?.vendor, approval?.customer, approval?.external];
  const present = parties.filter((p) => p !== null && p !== undefined);
  if (present.every((p) => p!.status === ApprovalStatus.Approved)) {
    return ApprovalStatus.Approved;
  }
  if (present.some((p) => p!.status === ApprovalStatus.Rejected)) {
    return ApprovalStatus.Rejected;
  }
  return ApprovalStatus.Pending;
}

export function designSortableState(design: DesignDto): SortableState {
  if (design.status === DesignStatus.Approval && design.approval) {
    switch (apiOverallApprovalStatus(design.approval)) {
      case ApprovalStatus.Approved:
        return "Approved";
      case ApprovalStatus.Rejected:
        return "Rejected";
      default:
        return "Pending";
    }
  }
  switch (design.status) {
    case DesignStatus.Approval:
      return "Pending";
    case DesignStatus.Done:
      return "Approved";
    case DesignStatus.Rejected:
      return "Rejected";
    default:
      return "Draft";
  }
}

// The stateFilter value a list key carries (["designs", scope, "<qs>"] with
// "...filterByField=stateFilter&filterByValue=Pending..."), or undefined
// when the page is unfiltered. Pairs the repeated filterByField/
// filterByValue segments by position, as toQueryParam writes them; the API
// parses the value case-insensitively, so the comparison is too.
export function listKeyStateFilter(
  key: readonly unknown[]
): SortableState | undefined {
  const qs = key[2];
  if (typeof qs !== "string" || qs.length === 0) return undefined;
  const params = new URLSearchParams(qs.startsWith("?") ? qs.slice(1) : qs);
  const fields = params.getAll("filterByField");
  const values = params.getAll("filterByValue");
  const index = fields.findIndex((f) => f.toLowerCase() === "statefilter");
  if (index < 0) return undefined;
  const value = (values[index] ?? "").toLowerCase();
  const known: SortableState[] = ["Draft", "Pending", "Approved", "Rejected"];
  return known.find((s) => s.toLowerCase() === value);
}

// --- In-flight guard ------------------------------------------------------
//
// React Query does not discard a fetch that was in flight when setQueryData
// ran: its result (read by the server BEFORE the verdict committed, e.g. the
// GetDesigns that updateDesign's invalidate started, or a stale list
// revalidating on mount) lands afterwards and overwrites the patch, and the
// page is then "fresh" for five minutes with the OLD status. Cancelling is
// not an option either: a first-load page has no data to revert to and
// would sit in "loading" forever. So the patch is re-applied to the landed
// data instead (a `success` update that is not our own manual write), which
// keeps every other row's fresh data and costs no call.
const REAPPLY_MAX_WAIT_MS = 5 * 60 * 1000;

type CacheEvent = Parameters<Parameters<QueryCache["subscribe"]>[0]>[0];

function isFetching(query: Query): boolean {
  return query.state.fetchStatus === "fetching";
}

function reapplyWhenInFlightLands(
  queryClient: QueryClient,
  inFlight: Query[],
  fresh: DesignDto
): void {
  if (inFlight.length === 0) return;
  const pending = new Set(inFlight.map((q) => q.queryHash));
  const cache = queryClient.getQueryCache();
  let unsubscribe: (() => void) | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const stop = () => {
    if (unsubscribe) unsubscribe();
    unsubscribe = undefined;
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  };
  const listener = (event: CacheEvent) => {
    if (!pending.has(event.query.queryHash)) return;
    if (event.type === "removed") {
      pending.delete(event.query.queryHash);
    } else if (event.type === "updated") {
      const action = event.action as { type: string; manual?: boolean };
      if (action.type === "success" && !action.manual) {
        pending.delete(event.query.queryHash);
        applyToQuery(queryClient, event.query, fresh);
      } else if (action.type === "error") {
        pending.delete(event.query.queryHash);
      }
    }
    if (pending.size === 0) stop();
  };
  unsubscribe = cache.subscribe(listener);
  timer = setTimeout(stop, REAPPLY_MAX_WAIT_MS);
}

export interface DesignPatchResult {
  // Entries rewritten with the merged design.
  written: number;
  // Rows dropped from status-filtered pages the design no longer belongs to.
  removed: number;
  // Pages marked stale (no refetch) so paging/membership heal on next mount.
  invalidated: number;
  // Queries that were fetching; the patch is re-applied when they land.
  inFlight: number;
}

function markStaleNoRefetch(queryClient: QueryClient, key: readonly unknown[]) {
  queryClient.invalidateQueries({
    queryKey: key as unknown[],
    exact: true,
    refetchType: "none",
  });
}

// Writes `data` under the query's key WITHOUT touching its freshness: the
// entry keeps its own dataUpdatedAt, and an invalidation that was pending
// stays pending (setQueryData would otherwise clear it). Exported for the
// cart price list sync (cartPriceListSync), which patches list rows with the
// same guarantee; no behaviour change here.
export function writePreservingFreshness(
  queryClient: QueryClient,
  query: Query,
  data: unknown
) {
  const wasInvalidated = query.state.isInvalidated;
  queryClient.setQueryData(query.queryKey, data, {
    updatedAt: query.state.dataUpdatedAt || undefined,
  });
  if (wasInvalidated) markStaleNoRefetch(queryClient, query.queryKey);
}

// One query, one design: the detail/cart entry is merged in its own shape;
// a list page gets its row merged, or dropped when a stateFilter on the
// page no longer matches (totalCount decremented, page marked stale so the
// next mount fills the gap); a filtered page the design now belongs to but
// is not on is marked stale (no refetch - the row appears on next mount).
function applyToQuery(
  queryClient: QueryClient,
  query: Query,
  fresh: DesignDto
): Partial<DesignPatchResult> {
  const key = query.queryKey;
  const current = query.state.data;
  if (current === null || current === undefined) return {};
  if (isDesignDetailQueryKey(key)) {
    if (key[2] !== fresh.id) return {};
    writePreservingFreshness(queryClient, query, patchDesignEntry(current, fresh));
    return { written: 1 };
  }
  if (!isPagedResponse(current)) return {};
  const filter = listKeyStateFilter(key);
  const onPage = current.results.some((row) => row && row.id === fresh.id);
  if (!onPage) {
    if (filter && designSortableState(fresh) === filter) {
      markStaleNoRefetch(queryClient, key);
      return { invalidated: 1 };
    }
    return {};
  }
  const results = current.results.map((row) =>
    row && row.id === fresh.id ? mergeDesignDto(row, fresh) : row
  );
  if (filter) {
    const mergedRow = results.find((row) => row && row.id === fresh.id)!;
    if (designSortableState(mergedRow) !== filter) {
      writePreservingFreshness(queryClient, query, {
        ...current,
        results: results.filter((row) => !row || row.id !== fresh.id),
        totalCount: Math.max(0, (current.totalCount ?? 1) - 1),
      });
      markStaleNoRefetch(queryClient, key);
      return { removed: 1, invalidated: 1 };
    }
  }
  writePreservingFreshness(queryClient, query, { ...current, results });
  return { written: 1 };
}

// Lands the response in every cached copy of the design under the
// ["designs"] prefix: each list page that holds the row (all scopes - the
// approval state is scope-independent), every detail entry with that id
// (DesignRepository.useDesign and the cart's copies, any act-as scope).
// Absent entries are NOT seeded: a seeded-but-inactive detail entry would
// never heal (refetchOnMount is off globally). Synchronous: the caches are
// consistent when this returns; in-flight queries are handled as they land.
export function patchDesignInQueryCache(
  queryClient: QueryClient,
  fresh: DesignDto
): DesignPatchResult {
  const result: DesignPatchResult = {
    written: 0,
    removed: 0,
    invalidated: 0,
    inFlight: 0,
  };
  const inFlight: Query[] = [];
  queryClient
    .getQueryCache()
    .findAll({ queryKey: ["designs"] })
    .forEach((query) => {
      const key = query.queryKey;
      const isDetail = isDesignDetailQueryKey(key);
      if (isDetail && key[2] !== fresh.id) return;
      if (!isDetail && typeof key[2] !== "string") return;
      if (isFetching(query)) inFlight.push(query);
      const part = applyToQuery(queryClient, query, fresh);
      result.written += part.written ?? 0;
      result.removed += part.removed ?? 0;
      result.invalidated += part.invalidated ?? 0;
    });
  result.inFlight = inFlight.length;
  reapplyWhenInFlightLands(queryClient, inFlight, fresh);
  return result;
}

// Delete: drop the row from every list page in place (totalCount
// decremented; pageCount is recomputed by the next mount), drop the
// INACTIVE detail entries, and refetch the ACTIVE ones (the cart's, an open
// editor's) so their observers see the same 404 they see today - that is
// the only call, and only while the deleted design is on screen somewhere.
// Lists are marked stale WITHOUT refetching so paging heals on the next
// mount (the design list refetches stale data on mount). Returns the number
// of list pages the row was removed from.
export function removeDesignFromQueryCache(
  queryClient: QueryClient,
  designId: number
): number {
  let removed = 0;
  queryClient
    .getQueryCache()
    .findAll({ queryKey: ["designs"] })
    .forEach((query) => {
      const key = query.queryKey;
      const current = query.state.data;
      if (isDesignDetailQueryKey(key)) {
        if (key[2] !== designId) return;
        if (query.isActive()) {
          queryClient.invalidateQueries({ queryKey: key, exact: true }).then();
        } else {
          queryClient.removeQueries({ queryKey: key, exact: true });
        }
        return;
      }
      if (!isPagedResponse(current)) return;
      if (!current.results.some((row) => row && row.id === designId)) return;
      writePreservingFreshness(queryClient, query, {
        ...current,
        results: current.results.filter((row) => !row || row.id !== designId),
        totalCount: Math.max(0, (current.totalCount ?? 1) - 1),
      });
      removed += 1;
    });
  queryClient.invalidateQueries({
    queryKey: ["designs"],
    refetchType: "none",
    predicate: (query) => !isDesignDetailQueryKey(query.queryKey),
  });
  return removed;
}

// --- Save patch (flag designSavePatchEnabled) ------------------------------
//
// PUT .../designs/{id} answers with the design after the save. Its placed
// rows, measurement lines, title, thumbnail and updateAtUtc are the truth and
// land via patchDesignInQueryCache like a verdict does; its prices are the
// persisted-only rows (UpdateDesign never runs AdjustPrice), so the price
// rule above keeps the cached ones - which is only right while the placed
// LOGOS are the ones those cached prices were computed for: the same set of
// rows AND every logo unchanged since the cache was filled. A logo's price
// follows its colour count, size and method, all edited in the logo editor
// (which invalidates ['logos'], never ['designs']), so a logo edited after
// the cache was filled shows up as a newer logo.updatedAtUtc in the PUT
// response (each row's logo is read fresh from the DB) and needs the
// server's AdjustPrice, i.e. today's refetch - as does a save that added or
// removed a logo. The row ids of a placed row are stable across saves
// (UpdateDesignAsync updates rows in place and only adds a row for an
// incoming id 0), so the comparison is on row ids, with the logo id as the
// fallback for a cached row without one (a mapped cart entry carries
// updateAtUtc as a Date, the raw DTO updatedAtUtc as a string - both are
// compared as instants). No cached detail entry (a list-side save), or a
// row whose logo stamp is unknown on either side = unknown = false.
function logoStampMs(row: { logo?: unknown } | null): number | undefined {
  const logo = (row?.logo ?? null) as
    | { updatedAtUtc?: unknown; updateAtUtc?: unknown }
    | null;
  const stamp = logo?.updatedAtUtc ?? logo?.updateAtUtc;
  if (stamp === null || stamp === undefined || stamp === "") return undefined;
  const ms =
    stamp instanceof Date ? stamp.getTime() : new Date(String(stamp)).getTime();
  return Number.isNaN(ms) ? undefined : ms;
}

function placedRowKeys(rows: unknown): string[] | undefined {
  if (!Array.isArray(rows)) return undefined;
  const keys: string[] = [];
  for (const row of rows) {
    const r = row as { id?: unknown; logoId?: unknown; logo?: unknown } | null;
    if (!r) {
      keys.push("");
      continue;
    }
    const stamp = logoStampMs(r);
    if (stamp === undefined) return undefined;
    const rowKey =
      typeof r.id === "number" && r.id > 0
        ? `row:${r.id}`
        : `logo:${String(r.logoId ?? "")}`;
    keys.push(`${rowKey}@${stamp}`);
  }
  return keys.sort();
}

export function savedDesignLogoSetMatchesCache(
  queryClient: QueryClient,
  fresh: DesignDto
): boolean {
  const freshKeys = placedRowKeys(fresh.designLogos);
  if (!freshKeys) return false;
  const detail = queryClient
    .getQueryCache()
    .findAll({ queryKey: ["designs"] })
    .find(
      (query) =>
        isDesignDetailQueryKey(query.queryKey) &&
        query.queryKey[2] === fresh.id &&
        query.state.data !== null &&
        query.state.data !== undefined
    );
  if (!detail) return false;
  const cachedKeys = placedRowKeys(
    (detail.state.data as { designLogos?: unknown }).designLogos
  );
  if (!cachedKeys) return false;
  return (
    cachedKeys.length === freshKeys.length &&
    cachedKeys.every((key, index) => key === freshKeys[index])
  );
}
